/**
 * PATH: packages/email-hub/src/feature/components/AttachmentList.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-ATTACHMENTLIST-001
 * PURPOSE: Canonical body of this email components module. Lifted
 *          from apps/<tenant>/src/features/email/components/AttachmentList.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/AttachmentList.tsx: thin re-export
 *     shim that points back here.
 */

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, Loader2, Paperclip } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { emailApiFetch } from '../lib/api'
import { formatFileSize, isUserVisibleAttachment } from '../lib/utils'
import type { EmailAttachment } from '../hooks/useEmails'
import AttachmentChip from './AttachmentChip'
import AttachmentContextMenu from './AttachmentContextMenu'
import { useMarkEmailAsBill } from '../../_stubs/bills/hooks/useBills'

/**
 * AttachmentList
 * --------------
 *
 * Container for the row of attachment chips that sits above the
 * email body. Owns:
 *
 *   - Header summary (count + total size + "download all" action)
 *   - Grid layout responsive to the available width
 *   - The "open preview" callback bubble-up (chips themselves are
 *     dumb, the parent decides which preview is active)
 *   - Right-click context menu state (which chip is open + the
 *     viewport coordinates) so only one menu is ever visible
 *   - The "Allocate to Bills" side-effect: POST mark-as-bill, toast
 *     the result, and navigate the user straight to ``/bills/<id>``
 *
 * "Download all" runs the chips' downloads sequentially with a
 * 250 ms gap so most browsers don't pop the "this site is
 * downloading lots of files" interstitial.
 */

interface AttachmentListProps {
  attachments: EmailAttachment[]
  emailId: string
  onPreview: (index: number) => void
  /** Email→Quote ARC § 3.3 (P3) — optional map of attachment id →
   *  download progress (0–100). When supplied, each chip whose id
   *  appears in the map renders the in-flight progress overlay
   *  (badge + bottom bar). Missing entries render the chip as-is.
   *  Defaults to undefined; Scope 2A (P5/P6) wires the live feed in. */
  progressByAttachmentId?: Record<string | number, number | null | undefined>
}

interface MenuState {
  attachmentId: string | number
  index: number
  x: number
  y: number
}

export default function AttachmentList({
  attachments: rawAttachments,
  emailId,
  onPreview,
  progressByAttachmentId,
}: AttachmentListProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [bulkBusy, setBulkBusy] = useState(false)
  const [menu, setMenu] = useState<MenuState | null>(null)
  const markAsBill = useMarkEmailAsBill()

  // Filter out inline attachments (rendered IN the body via cid:). See
  // the `isUserVisibleAttachment` docstring above for why. Memoised so
  // the chip grid + counts + bulk-download list all derive from the
  // same source and stay in lock-step on re-render.
  const attachments = useMemo(
    () => rawAttachments.filter(isUserVisibleAttachment),
    [rawAttachments],
  )

  const totalSize = useMemo(
    () => attachments.reduce((acc, a) => acc + (a.size || 0), 0),
    [attachments],
  )

  const downloadable = useMemo(
    () => attachments.filter(a => !!a.download_url),
    [attachments],
  )

  // ----- Per-attachment actions used by the context menu -----------

  const downloadOne = useCallback(
    async (att: EmailAttachment) => {
      if (!att.download_url) return
      try {
        const res = await emailApiFetch(att.download_url)
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = att.filename || 'attachment'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Download failed'
        toast(`Could not download ${att.filename}: ${msg}`, 'error')
      }
    },
    [toast],
  )

  const openInNewTab = useCallback(
    async (att: EmailAttachment) => {
      // Same auth-gated fetch + blob URL pattern as the preview
      // modal so we don't 401. The browser pops the new tab in the
      // same user gesture, which is why we open *before* awaiting
      // (some browsers blockpopups otherwise — but we need the bytes
      // for the URL, so we open a placeholder tab first and then
      // navigate it once the blob is ready).
      if (!att.download_url) return
      const placeholder = window.open('about:blank', '_blank')
      try {
        const res = await emailApiFetch(att.download_url)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        if (placeholder) {
          placeholder.location.href = objectUrl
        }
        // Don't revoke immediately — user is now viewing it.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60_000)
      } catch (err) {
        if (placeholder) placeholder.close()
        const msg = err instanceof Error ? err.message : 'Open failed'
        toast(`Could not open ${att.filename}: ${msg}`, 'error')
      }
    },
    [toast],
  )

  const copyFilename = useCallback(
    async (att: EmailAttachment) => {
      try {
        await navigator.clipboard.writeText(att.filename || '')
        toast(`Copied filename`, 'success')
      } catch {
        toast(`Could not copy filename`, 'error')
      }
    },
    [toast],
  )

  // ----- Email→Quote ARC § 3.4 (P4 / Scope 1C) — QDrive handlers ----
  //
  // The serializer surfaces ``qdrive_file_id`` (UUID of the FileRecord
  // the attachment was ingested into) + ``qdrive_ref_code`` (short
  // shareable cross-hub code). We build the canonical deep-link URL
  // here so the menu stays a dumb presentational component.
  //
  // URL shape: ``/qdrive?file_id=<uuid>&ref=email&email_id=<eid>``.
  // ``ref=email`` tells QDrivePage where the user came from so it
  // can render a "back to email" affordance (TODO in a follow-up
  // MR — for now the param is just a breadcrumb in the URL bar). We
  // include ``email_id`` so a copy-pasted link can recreate the
  // source-of-truth context.

  const buildQDriveUrl = useCallback(
    (att: EmailAttachment): string | null => {
      if (!att.qdrive_file_id) return null
      const params = new URLSearchParams()
      params.set('file_id', att.qdrive_file_id)
      params.set('ref', 'email')
      params.set('email_id', emailId)
      return `/qdrive?${params.toString()}`
    },
    [emailId],
  )

  const openInQDrive = useCallback(
    (att: EmailAttachment) => {
      const url = buildQDriveUrl(att)
      if (!url) {
        toast(`This attachment isn't in QDrive yet`, 'info')
        return
      }
      // SPA-internal navigation so we keep the user inside the
      // dashboard shell. ``navigate`` defers to the host app's
      // <Route path="/qdrive"> which mounts QDrivePage.
      navigate(url)
    },
    [buildQDriveUrl, navigate, toast],
  )

  const copyQDriveLink = useCallback(
    async (att: EmailAttachment) => {
      const url = buildQDriveUrl(att)
      if (!url) {
        toast(`This attachment isn't in QDrive yet`, 'info')
        return
      }
      // Build the absolute URL so a copy-paste into Slack / email
      // / Notion resolves to the right tenant when the recipient
      // opens it on a different device.
      const absolute =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}${url}`
          : url
      try {
        await navigator.clipboard.writeText(absolute)
        toast(`Copied QDrive link`, 'success')
      } catch {
        toast(`Could not copy QDrive link`, 'error')
      }
    },
    [buildQDriveUrl, toast],
  )

  const copyQDriveRef = useCallback(
    async (att: EmailAttachment) => {
      if (!att.qdrive_ref_code) {
        toast(`This attachment has no QDrive ref yet`, 'info')
        return
      }
      try {
        await navigator.clipboard.writeText(att.qdrive_ref_code)
        toast(`Copied ${att.qdrive_ref_code}`, 'success')
      } catch {
        toast(`Could not copy ref`, 'error')
      }
    },
    [toast],
  )

  const allocateToBill = useCallback(
    async (att: EmailAttachment) => {
      const result = await markAsBill.run(emailId, att.id)
      if (!result) {
        toast(
          markAsBill.error ||
            `Could not allocate ${att.filename} to Bills`,
          'error',
        )
        return
      }
      toast(
        result.created
          ? `Allocated to Bills - opening bill…`
          : `This email is already in Bills - opening…`,
        'success',
      )
      // Close menu, then navigate. The Billing Hub layout activates
      // the Bills tab automatically when the URL is /bills/<uuid>.
      setMenu(null)
      navigate(`/bills/${result.bill_id}`)
    },
    [emailId, markAsBill, navigate, toast],
  )

  // ----- Bulk download (existing) ----------------------------------

  async function downloadAll() {
    if (bulkBusy || downloadable.length === 0) return
    setBulkBusy(true)
    let ok = 0
    let failed = 0
    try {
      for (const att of downloadable) {
        try {
          const res = await emailApiFetch(att.download_url!)
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const blob = await res.blob()
          const objectUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = objectUrl
          a.download = att.filename || 'attachment'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
          ok += 1
          await new Promise(r => setTimeout(r, 250))
        } catch (err) {
          failed += 1
          console.warn('Bulk download failed for', att.filename, err)
        }
      }
      if (failed === 0) {
        toast(`Downloaded ${ok} attachment${ok === 1 ? '' : 's'}`, 'success')
      } else if (ok === 0) {
        toast(`Could not download attachments`, 'error')
      } else {
        toast(`Downloaded ${ok} of ${ok + failed}; ${failed} failed`, 'info')
      }
    } finally {
      setBulkBusy(false)
    }
  }

  // Resolve the active menu's attachment by id so a stale menu can
  // never reference a deleted chip (e.g. if the email refetches mid-
  // menu and the attachment list shrinks). Declared before the early
  // return so the hooks order stays stable across renders.
  const activeAttachment = useMemo(() => {
    if (!menu) return null
    return attachments.find(a => a.id === menu.attachmentId) ?? null
  }, [menu, attachments])

  if (attachments.length === 0) return null

  const count = attachments.length
  const showBulk = downloadable.length > 1

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-gradient-to-b from-muted/40 to-muted/10',
        'p-4 mb-6',
      )}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Paperclip className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground leading-tight">
              {count} attachment{count === 1 ? '' : 's'}
            </p>
            {totalSize > 0 && (
              <p className="text-[11px] text-muted-foreground leading-tight">
                {formatFileSize(totalSize)} total · right-click for actions
              </p>
            )}
          </div>
        </div>

        {showBulk && (
          <button
            type="button"
            onClick={downloadAll}
            disabled={bulkBusy}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md',
              'border border-border bg-background hover:bg-accent transition-colors',
              'disabled:opacity-60 disabled:cursor-not-allowed shrink-0',
            )}
            title="Download all attachments"
          >
            {bulkBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Download all
          </button>
        )}
      </div>

      {/*
        Column count derives from the CONTAINER, not the viewport. The old
        `sm:grid-cols-2 xl:grid-cols-3` forced 3 columns whenever the WINDOW
        was xl — but this list also renders inside narrow side panes (e.g.
        the billing workspace email split), where 3 columns crushed each
        chip to ~160px and the filename/size/QDrive badge overlapped
        (operator 2026-07-24 "overlapping attachment ui"). auto-fill +
        minmax gives every chip ≥ 240px and adds columns only when the
        container can actually fit them.
      */}
      <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
        {attachments.map((att, idx) => (
          <AttachmentChip
            key={att.id}
            attachment={att}
            progress={progressByAttachmentId?.[att.id]}
            onPreview={() => onPreview(idx)}
            onContextMenu={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMenu({
                attachmentId: att.id,
                index: idx,
                x: e.clientX,
                y: e.clientY,
              })
            }}
          />
        ))}
      </div>

      {menu && activeAttachment && (
        <AttachmentContextMenu
          attachment={activeAttachment}
          x={menu.x}
          y={menu.y}
          allocatingToBill={markAsBill.busy}
          onClose={() => setMenu(null)}
          onPreview={() => onPreview(menu.index)}
          onDownload={() => downloadOne(activeAttachment)}
          onAllocateToBill={() => allocateToBill(activeAttachment)}
          onCopyFilename={() => copyFilename(activeAttachment)}
          onOpenInNewTab={() => openInNewTab(activeAttachment)}
          onOpenInQDrive={() => openInQDrive(activeAttachment)}
          onCopyQDriveLink={() => copyQDriveLink(activeAttachment)}
          onCopyQDriveRef={() => copyQDriveRef(activeAttachment)}
        />
      )}
    </div>
  )
}
